import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Input,
  Spinner,
} from "reactstrap";
import { X } from "react-feather";
import axios from "axios";
import { baseURL } from "../../../../../Services/api/baseURL";
import { toast } from "react-toastify";

const CustomerTagModal = ({ isOpen, toggle, customer, onTagsUpdated }) => {
  const [allTags, setAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && customer) {
      fetchTags();
      // Extract tag IDs from customer.tags array
      const tagIds = customer.tags?.map(tag => tag._id) || [];
      setSelectedTags(tagIds);
    }
  }, [isOpen, customer]);

  const fetchTags = async () => {
    setIsLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const response = await axios.get(`${baseURL}/api/tags`, {
        headers: { Authorization: token },
      });

      if (response.status === 200) {
        setAllTags(response.data.data);
      }
    } catch (error) {
      toast.error("Failed to fetch tags");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleTag = (tagId) => {
    setSelectedTags((prev) => {
      if (prev.includes(tagId)) {
        return prev.filter((id) => id !== tagId);
      } else {
        return [...prev, tagId];
      }
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const customerId = customer._id || customer.id; // Handle both _id and id
      const response = await axios.put(
        `${baseURL}/api/tags/customers/${customerId}/tags`,
        { tagIds: selectedTags },
        { headers: { Authorization: token } }
      );

      if (response.status === 200) {
        toast.success("Tags updated successfully");
        if (onTagsUpdated) {
          onTagsUpdated(customer._id, selectedTags);
        }
        toggle();
      }
    } catch (error) {
      toast.error("Failed to update tags");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const filteredTags = allTags.filter((tag) =>
    tag.display_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isTagSelected = (tagId) => selectedTags.includes(tagId);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Manage Tags for{" "}
        {customer?.firstName
          ? `${customer.firstName} ${customer.lastName || ""}`
          : "Customer"}
      </ModalHeader>
      <ModalBody>
        {/* Search */}
        <div className="mb-3">
          <Input
            type="text"
            placeholder="Search tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-control"
          />
        </div>

        {/* Selected Tags */}
        {selectedTags.length > 0 && (
          <div className="mb-3">
            <small className="text-muted d-block mb-2">
              Selected Tags ({selectedTags.length})
            </small>
            <div className="d-flex flex-wrap gap-2">
              {allTags
                .filter((tag) => selectedTags.includes(tag._id))
                .map((tag) => (
                  <Badge
                    key={tag._id}
                    style={{
                      backgroundColor: tag.color,
                      color: "#fff",
                      padding: "6px 12px",
                      fontSize: "0.85rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleToggleTag(tag._id)}
                  >
                    {tag.display_name}
                    <X size={14} />
                  </Badge>
                ))}
            </div>
          </div>
        )}

        {/* Available Tags */}
        <div>
          <small className="text-muted d-block mb-2">Available Tags</small>
          {isLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : (
            <div
              style={{
                maxHeight: "400px",
                overflowY: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                padding: "12px",
              }}
            >
              {filteredTags.length === 0 ? (
                <p className="text-muted text-center mb-0">No tags found</p>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {filteredTags.map((tag) => {
                    const selected = isTagSelected(tag._id);
                    return (
                      <div
                        key={tag._id}
                        onClick={() => handleToggleTag(tag._id)}
                        style={{
                          padding: "12px",
                          borderRadius: "8px",
                          border: selected
                            ? `2px solid ${tag.color}`
                            : "1px solid #e0e0e0",
                          backgroundColor: selected
                            ? `${tag.color}15`
                            : "#fff",
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                        className="hover-shadow"
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center gap-2">
                            <span
                              style={{
                                width: "16px",
                                height: "16px",
                                borderRadius: "50%",
                                backgroundColor: tag.color,
                              }}
                            ></span>
                            <div>
                              <div style={{ fontWeight: "500" }}>
                                {tag.display_name}
                              </div>
                              {tag.description && (
                                <small className="text-muted">
                                  {tag.description}
                                </small>
                              )}
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-3">
                            <Badge color="light" pill>
                              {tag.customer_count || 0} customers
                            </Badge>
                            <Input
                              type="checkbox"
                              checked={selected}
                              onChange={() => handleToggleTag(tag._id)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={isSaving}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Spinner size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CustomerTagModal;